import {
  GridCellEditStopParams,
  GridRenderEditCellParams,
} from '@mui/x-data-grid-premium'
import { GridCellParams, GridColDef, MuiEvent } from '@mui/x-data-grid-pro'
import { GridApiPro } from '@mui/x-data-grid-pro/models/gridApiPro'
import { styleConstants } from '@paywithextend/components'
import Button from 'ext-ui/components/Button/Button'
import {
  base_colorContentBorder,
  grey700,
  red300,
  red400,
  text_colorTextPrimary,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import { font_md, font_sm } from 'ext-ui/style/typography'
import React, { MutableRefObject, useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import DataGrid, { DataGridRowHeight } from 'src/components/DataGrid/DataGrid'
import SvgTrash from 'src/components/Svgs/SvgTrash'
import SplitValueInputCell from 'src/components/TransactionsDataGrid/cells/SplitValueInputCell'
import TransactionExpenseCategoryCell from 'src/components/TransactionsDataGrid/cells/TransactionExpenseCategoryCell'
import TransactionExpenseCategoryDropdownCell from 'src/components/TransactionsDataGrid/cells/TransactionExpenseCategoryDropdownCell'
import {
  ExpenseCategoryCellData,
  ExpenseCategoryFieldKey,
  TransactionExpenseCategorySplit,
} from 'src/components/TransactionsDataGrid/transactionsDataGrid.types'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import {
  activeAlphabeticalExpenseCategoryFiltersRequestParams,
  ExpenseCategoryType,
  useQueryExpenseCategories,
} from 'src/queries/organizations/expenseCategories.queries'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { css } from 'styled-components'

const EDIT_MODE = 'cell'
const MAX_VISIBLE_ROWS = 50
const MAX_TOTAL_SPLITS = 50
const GRID_HEADER_HEIGHT = 44
const DATA_GRID_PADDING = 60

interface SplitExpenseCategoriesRowModel {
  id: string
  splitIndex: number
  splitValue: string
  transaction: { id: string }
  expenseCategoryCellData: Record<
    ExpenseCategoryFieldKey,
    ExpenseCategoryCellData
  >
  isPlaceholder?: boolean
}

// Generate stable row ids for the grid, falling back when crypto is unavailable.
const createRowKey = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `split-${Date.now()}-${Math.random().toString(36).slice(2)}`

type SplitExpenseCategoriesDataGridProps = {
  apiRef?: MutableRefObject<GridApiPro | null>
  transactionId?: string
  splits: TransactionExpenseCategorySplit[]
  onChange: (splits: TransactionExpenseCategorySplit[]) => void
  totalAmountCents?: number | null
  onSave?: (splits: TransactionExpenseCategorySplit[]) => void
  splitError?: string
  saving?: boolean
  disableSave?: boolean
  isLocked?: boolean
}

// Parse a split value into a safe numeric value (invalid inputs become 0).
const getSplitNumericValue = (value?: string | number | null): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0
  }
  if (!value) return 0
  const numeric = Number.parseFloat(String(value))
  return Number.isFinite(numeric) ? numeric : 0
}

const SplitExpenseCategoriesDataGrid: React.FC<
  SplitExpenseCategoriesDataGridProps
> = (props) => {
  const translate = useTranslate()
  const formatCurrency = useCurrencyFormatter()
  const activeIntegration = useSelector(selectActiveOrgIntegration)

  const { data: orgExpCats, isSuccess: isOrgExpCatsSuccess } =
    useQueryExpenseCategories({
      filters: activeAlphabeticalExpenseCategoryFiltersRequestParams,
    })

  const vendorCategory = useMemo(
    () =>
      (orgExpCats ?? []).find(
        (category) => category.type === ExpenseCategoryType.Vendor,
      ),
    [orgExpCats],
  )
  const departmentCategory = useMemo(
    () =>
      (orgExpCats ?? []).find((category) => category.code === 'tc_DEPARTMENTS'),
    [orgExpCats],
  )
  // Department sharing depends on the integration.
  const isDepartmentSharedAcrossSplits = useMemo(
    () =>
      [
        'QUICKBOOKS_ONLINE',
        'QUICKBOOKS_DESKTOP',
        'QUICKBOOKS_ONLINE_SANDBOX',
      ].includes(activeIntegration?.connectedPlatform ?? ''),
    [activeIntegration?.connectedPlatform],
  )

  // Vendor is always shared across splits; departments are shared for some integrations.
  const lockedSharedCategoryIds = useMemo(() => {
    const ids: string[] = []
    if (vendorCategory?.id) {
      ids.push(vendorCategory.id)
    }
    if (isDepartmentSharedAcrossSplits && departmentCategory?.id) {
      ids.push(departmentCategory.id)
    }
    return ids
  }, [
    departmentCategory?.id,
    isDepartmentSharedAcrossSplits,
    vendorCategory?.id,
  ])

  const splitExpenseCategories = useMemo(() => orgExpCats ?? [], [orgExpCats])

  // Maintain stable row keys.
  const rowKeysRef = React.useRef<string[]>([])
  const splitsRef = React.useRef(props.splits)

  const totalSplitAmount = React.useMemo(() => {
    const cents = props.totalAmountCents ?? 0
    return cents / 100
  }, [props.totalAmountCents])

  const isLocked = props.isLocked ?? false

  useEffect(() => {
    splitsRef.current = props.splits
  }, [props.splits])

  const totalSplitValue = useMemo(() => {
    return props.splits.reduce((acc, split) => {
      return acc + getSplitNumericValue(split?.splitValue)
    }, 0)
  }, [props.splits])

  const formattedSplitValue = useMemo(
    () => formatCurrency({ amount: totalSplitValue }),
    [formatCurrency, totalSplitValue],
  )

  const formattedTotalAmount = useMemo(
    () => formatCurrency({ amount: totalSplitAmount }),
    [formatCurrency, totalSplitAmount],
  )

  // Ensure each split index maps to the same row id across renders.
  const ensureRowKey = useCallback((index: number) => {
    if (!rowKeysRef.current[index]) {
      rowKeysRef.current[index] = createRowKey()
    }
    return rowKeysRef.current[index]
  }, [])

  // Create a split with no value or mappings.
  const createEmptySplit = useCallback(
    (): TransactionExpenseCategorySplit => ({
      splitValue: '',
      expenseCategories: {},
    }),
    [],
  )

  // Normalize mapping fields so comparisons do not need to handle undefined.
  const normalizeMappingValues = useCallback(
    (mapping?: { labelId?: string; labelName?: string }) => ({
      labelId: mapping?.labelId ?? '',
      labelName: mapping?.labelName ?? '',
    }),
    [],
  )

  // Defensive copy so edits never mutate the incoming props.
  const normalizeSplit = useCallback(
    (
      split?: TransactionExpenseCategorySplit,
    ): TransactionExpenseCategorySplit => ({
      splitValue: split?.splitValue ?? '',
      expenseCategories: { ...(split?.expenseCategories ?? {}) },
    }),
    [],
  )

  // Locked categories can only be edited on the first split line.
  const isSharedLockedCategory = useCallback(
    (categoryId: string, splitIndex: number) =>
      splitIndex > 0 && lockedSharedCategoryIds.includes(categoryId),
    [lockedSharedCategoryIds],
  )

  // Copy locked categories from the source split into the target split.
  const applyLockedMappingsFromSplit = useCallback(
    (
      targetSplit: TransactionExpenseCategorySplit,
      sourceSplit?: TransactionExpenseCategorySplit,
    ): TransactionExpenseCategorySplit => {
      if (!sourceSplit || lockedSharedCategoryIds.length === 0) {
        return targetSplit
      }
      const nextMappings = { ...(targetSplit.expenseCategories ?? {}) }
      lockedSharedCategoryIds.forEach((categoryId) => {
        const mapping = sourceSplit.expenseCategories?.[categoryId]
        if (!mapping?.labelId && !mapping?.labelName) {
          delete nextMappings[categoryId]
          return
        }
        nextMappings[categoryId] = {
          categoryId,
          labelId: mapping.labelId || undefined,
          labelName: mapping.labelName || undefined,
        }
      })
      return {
        ...targetSplit,
        expenseCategories: nextMappings,
      }
    },
    [lockedSharedCategoryIds],
  )

  // Update a split in place and emit the full split list to the parent.
  const updateSplitAtIndex = useCallback(
    (
      splitIndex: number,
      updater: (
        split: TransactionExpenseCategorySplit,
      ) => TransactionExpenseCategorySplit,
    ): TransactionExpenseCategorySplit | undefined => {
      if (splitIndex < 0) return undefined
      const next = splitsRef.current.map((split) => normalizeSplit(split))
      if (!next[splitIndex]) {
        return undefined
      }
      next[splitIndex] = updater(next[splitIndex])
      props.onChange(next)
      return next[splitIndex]
    },
    [normalizeSplit, props.onChange],
  )

  // Build per-cell data needed by the category renderers.
  const buildExpenseCategoryCellData = useCallback(
    (
      split: TransactionExpenseCategorySplit,
    ): Record<ExpenseCategoryFieldKey, ExpenseCategoryCellData> => {
      if (!splitExpenseCategories) return {}
      return splitExpenseCategories.reduce(
        (acc, category) => {
          const mapping = split.expenseCategories?.[category.id]
          const valueText = mapping?.labelName ?? ''
          const labelIdOrFreeText = mapping?.labelId ?? mapping?.labelName
          acc[`ec:${category.id}` as ExpenseCategoryFieldKey] = {
            valueText,
            labelIdOrFreeText,
            showMissingWhenEmpty: false,
            appliedByAutomation: false,
            generatedByPrediction: false,
            errorMessage: undefined,
            cellType:
              category.freeTextAllowed && !category.hasLabels
                ? 'text'
                : 'select',
          }
          return acc
        },
        {} as Record<ExpenseCategoryFieldKey, ExpenseCategoryCellData>,
      )
    },
    [splitExpenseCategories],
  )

  const canAddRow = !isLocked && props.splits.length < MAX_TOTAL_SPLITS
  const canRemoveRows = !isLocked && props.splits.length > 1

  const rows: SplitExpenseCategoriesRowModel[] = useMemo(() => {
    if (!orgExpCats) return []
    return props.splits.map((split, index) => {
      const rowId = ensureRowKey(index)
      const normalized = normalizeSplit(split)
      const splitValueText =
        normalized.splitValue !== undefined && normalized.splitValue !== null
          ? String(normalized.splitValue)
          : ''
      return {
        id: rowId,
        splitIndex: index,
        splitValue: splitValueText,
        transaction: { id: props.transactionId ?? rowId },
        expenseCategoryCellData: buildExpenseCategoryCellData(normalized),
        isPlaceholder: false,
      }
    })
  }, [
    buildExpenseCategoryCellData,
    ensureRowKey,
    normalizeSplit,
    orgExpCats,
    props.splits,
    props.transactionId,
  ])

  // Add a new split line, inheriting any locked categories from the first split.
  const handleAddRow = useCallback(() => {
    if (!canAddRow || isLocked) return
    rowKeysRef.current[props.splits.length] = createRowKey()
    const normalizedSplits = props.splits.map((split) => normalizeSplit(split))
    const nextSplit = applyLockedMappingsFromSplit(
      createEmptySplit(),
      normalizedSplits[0],
    )
    props.onChange([...normalizedSplits, nextSplit])
  }, [
    applyLockedMappingsFromSplit,
    canAddRow,
    createEmptySplit,
    isLocked,
    normalizeSplit,
    props.onChange,
    props.splits,
  ])

  // Remove a split line.
  const handleRemoveRow = useCallback(
    (splitIndex: number) => {
      if (isLocked) return
      if (splitIndex < 0 || splitIndex >= props.splits.length) return
      if (props.splits.length <= 1) {
        rowKeysRef.current = [createRowKey()]
        props.onChange([createEmptySplit()])
        return
      }

      rowKeysRef.current.splice(splitIndex, 1)
      const next = props.splits.filter((_, idx) => idx !== splitIndex)
      props.onChange(next.map((split) => normalizeSplit(split)))
    },
    [createEmptySplit, isLocked, normalizeSplit, props.onChange, props.splits],
  )

  // Update the split amount directly.
  const handleSplitValueChange = useCallback(
    (splitIndex: number, value: string) => {
      if (isLocked) return
      updateSplitAtIndex(splitIndex, (split) => ({
        ...split,
        splitValue: value,
      }))
    },
    [isLocked, updateSplitAtIndex],
  )

  // Upsert or clear a category mapping for a split.
  const applySplitMapping = useCallback(
    (
      split: TransactionExpenseCategorySplit,
      categoryId: string,
      labelId?: string,
      labelName?: string,
    ): TransactionExpenseCategorySplit => {
      const nextMappings = { ...(split.expenseCategories ?? {}) }
      if (!labelId && !labelName) {
        delete nextMappings[categoryId]
      } else {
        nextMappings[categoryId] = {
          categoryId,
          labelId: labelId || undefined,
          labelName: labelName || undefined,
        }
      }
      return {
        ...split,
        expenseCategories: nextMappings,
      }
    },
    [],
  )

  // Enforce locked category mappings across all splits when the base split changes.
  const syncLockedCategoriesAcrossSplits = useCallback(() => {
    if (lockedSharedCategoryIds.length === 0 || props.splits.length === 0) {
      return
    }
    const baseSplit = props.splits[0]
    const hasMismatch = props.splits.some((split) =>
      lockedSharedCategoryIds.some((categoryId) => {
        const baseMapping = baseSplit?.expenseCategories?.[categoryId]
        const splitMapping = split.expenseCategories?.[categoryId]
        const normalizedBase = normalizeMappingValues(baseMapping)
        const normalizedSplit = normalizeMappingValues(splitMapping)
        return (
          normalizedBase.labelId !== normalizedSplit.labelId ||
          normalizedBase.labelName !== normalizedSplit.labelName
        )
      }),
    )
    if (!hasMismatch) return
    const nextSplits = props.splits.map((split) =>
      applyLockedMappingsFromSplit(normalizeSplit(split), baseSplit),
    )
    props.onChange(nextSplits)
  }, [
    applyLockedMappingsFromSplit,
    lockedSharedCategoryIds,
    normalizeMappingValues,
    normalizeSplit,
    props.onChange,
    props.splits,
  ])

  const expenseCategoryColumns = useMemo<
    Array<GridColDef<SplitExpenseCategoriesRowModel>>
  >(() => {
    if (!orgExpCats) return []
    const editable = !isLocked

    return splitExpenseCategories.map((category) => {
      const tooltipContent = lockedSharedCategoryIds.includes(category.id)
        ? category.code === 'tc_DEPARTMENTS' && isDepartmentSharedAcrossSplits
          ? translate('Departments cannot be split across lines')
          : category.type === ExpenseCategoryType.Vendor
            ? translate('Vendor cannot be split across lines')
            : undefined
        : undefined

      return {
        field: `ec:${category.id}` as ExpenseCategoryFieldKey,
        headerName: category.name,
        headerClassName: category.requiredForTransactions
          ? 'split-required'
          : undefined,
        description: tooltipContent,
        sortable: false,
        minWidth: 200,
        editable,
        valueGetter: (_value, row) =>
          row.expenseCategoryCellData[`ec:${category.id}`]?.labelIdOrFreeText,
        valueFormatter: (_value, row) =>
          row.expenseCategoryCellData[`ec:${category.id}`]?.valueText,
        cellClassName: (params) =>
          isSharedLockedCategory(category.id, params.row.splitIndex)
            ? 'split-locked'
            : '',
        renderCell: (
          params: GridCellParams<SplitExpenseCategoriesRowModel>,
        ) => (
          <TransactionExpenseCategoryCell
            params={params as GridCellParams<any>}
            onClear={() =>
              updateSplitAtIndex(params.row.splitIndex, (split) =>
                applySplitMapping(split, category.id),
              )
            }
            loading={false}
            editable={
              editable &&
              !isSharedLockedCategory(category.id, params.row.splitIndex)
            }
          />
        ),
        renderEditCell: (params) => {
          const baseParams = params as GridRenderEditCellParams<
            SplitExpenseCategoriesRowModel,
            string
          >
          return (
            <TransactionExpenseCategoryDropdownCell
              params={baseParams as GridRenderEditCellParams<any, string>}
              editMode={EDIT_MODE}
              category={category}
              loading={false}
              disabled={isSharedLockedCategory(
                category.id,
                params.row.splitIndex,
              )}
              dropdownContainer={
                typeof document !== 'undefined' ? document.body : undefined
              }
            />
          )
        },
      }
    })
  }, [
    applySplitMapping,
    isDepartmentSharedAcrossSplits,
    isSharedLockedCategory,
    lockedSharedCategoryIds,
    orgExpCats,
    splitExpenseCategories,
    translate,
    updateSplitAtIndex,
    isLocked,
  ])

  const splitValueColumn = useMemo<GridColDef<SplitExpenseCategoriesRowModel>>(
    () => ({
      field: 'splitValue',
      headerName: translate('Amount'),
      sortable: false,
      width: 110,
      minWidth: 110,
      maxWidth: 110,
      renderCell: (params) => (
        <SplitValueInputCell
          params={params}
          value={params.row.splitValue}
          onChange={(value) =>
            handleSplitValueChange(params.row.splitIndex, value)
          }
          disabled={isLocked}
        />
      ),
    }),
    [handleSplitValueChange, isLocked, translate],
  )

  const deleteColumn = useMemo<GridColDef<SplitExpenseCategoriesRowModel>>(
    () => ({
      field: 'splitDelete',
      headerName: '',
      sortable: false,
      minWidth: 48,
      maxWidth: 48,
      align: 'center',
      disableColumnMenu: true,
      renderCell: (params) => (
        <Button
          variant="no-style"
          aria-label={translate('Delete row')}
          disabled={!canRemoveRows}
          onClick={() => handleRemoveRow(params.row.splitIndex)}
          css={css`
            cursor: ${canRemoveRows ? 'pointer' : 'not-allowed'};
          `}
        >
          <SvgTrash fill={canRemoveRows ? red400 : grey700} />
        </Button>
      ),
    }),
    [canRemoveRows, handleRemoveRow, translate],
  )

  const columns = useMemo(() => {
    const baseColumns = [...expenseCategoryColumns, splitValueColumn]
    return isLocked ? baseColumns : [...baseColumns, deleteColumn]
  }, [expenseCategoryColumns, splitValueColumn, deleteColumn, isLocked])

  const pinnedRightColumns = useMemo(
    () => (isLocked ? ['splitValue'] : ['splitValue', 'splitDelete']),
    [isLocked],
  )

  const visibleRowCount = Math.min(rows.length || 1, MAX_VISIBLE_ROWS)
  const height =
    DATA_GRID_PADDING +
    GRID_HEADER_HEIGHT +
    visibleRowCount * DataGridRowHeight.Default

  const processRowUpdate = useCallback(
    async (
      newRow: SplitExpenseCategoriesRowModel & { [key: string]: any },
      oldRow: SplitExpenseCategoriesRowModel,
    ): Promise<SplitExpenseCategoriesRowModel> => {
      if (isLocked) {
        return oldRow
      }
      // Only respond to category edits; other fields are handled by custom cells.
      const changedEcField = Object.keys(newRow).find(
        (key) => key.startsWith('ec:') && newRow[key] !== (oldRow as any)[key],
      )

      if (!changedEcField) {
        return oldRow
      }

      const expenseCategoryId = changedEcField.substring(3)
      const value = newRow[changedEcField]
      const labelId = typeof value === 'object' ? value?.value : value
      const labelName = typeof value === 'object' ? value?.label : value

      if (lockedSharedCategoryIds.includes(expenseCategoryId)) {
        // Locked categories can only be edited on the first split.
        if (oldRow.splitIndex > 0) {
          return oldRow
        }
        // Apply the locked mapping to every split line.
        const nextSplits = props.splits.map((split) =>
          applySplitMapping(
            normalizeSplit(split),
            expenseCategoryId,
            labelId || undefined,
            labelName || undefined,
          ),
        )
        props.onChange(nextSplits)
        const updatedSplit =
          nextSplits[oldRow.splitIndex] ??
          normalizeSplit(props.splits[oldRow.splitIndex])
        return {
          ...newRow,
          isPlaceholder:
            oldRow.splitIndex >= props.splits.length
              ? false
              : newRow.isPlaceholder,
          expenseCategoryCellData: buildExpenseCategoryCellData(updatedSplit),
        }
      }

      // Non-locked category: update only the edited split line.
      const updatedSplit =
        updateSplitAtIndex(oldRow.splitIndex, (split) =>
          applySplitMapping(
            split,
            expenseCategoryId,
            labelId || undefined,
            labelName || undefined,
          ),
        ) ?? props.splits[oldRow.splitIndex]

      return {
        ...newRow,
        isPlaceholder:
          oldRow.splitIndex >= props.splits.length
            ? false
            : newRow.isPlaceholder,
        expenseCategoryCellData: buildExpenseCategoryCellData(updatedSplit),
      }
    },
    [
      applySplitMapping,
      buildExpenseCategoryCellData,
      isLocked,
      lockedSharedCategoryIds,
      normalizeSplit,
      props.onChange,
      props.splits,
      updateSplitAtIndex,
    ],
  )

  // Prevent automatic stop-edit on blur so dropdown selection is reliable.
  const handleCellEditStop = useCallback(
    async (
      params: GridCellEditStopParams<SplitExpenseCategoriesRowModel>,
      event: MuiEvent,
    ): Promise<void> => {
      if (
        params.reason === 'cellFocusOut' &&
        event &&
        event.defaultMuiPrevented !== true
      ) {
        event.defaultMuiPrevented = true
      }
    },
    [],
  )

  useEffect(() => {
    // Reset row ids when switching transactions.
    rowKeysRef.current = []
  }, [props.transactionId])

  useEffect(() => {
    // Always keep at least one split line to edit.
    if (props.splits.length === 0) {
      props.onChange([createEmptySplit()])
    }
  }, [props.onChange, props.splits.length, createEmptySplit])

  useEffect(() => {
    syncLockedCategoriesAcrossSplits()
  }, [syncLockedCategoriesAcrossSplits])

  if (!isOrgExpCatsSuccess) return <></>

  return (
    <div>
      <div
        css={css`
          border-bottom: 1px solid ${base_colorContentBorder};
        `}
      >
        <DataGrid
          apiRef={props.apiRef}
          height={height}
          rowHeight={DataGridRowHeight.Default}
          rows={rows}
          columns={columns}
          filtersBody={{} as Record<string, never>}
          disableColumnFilter
          disableColumnMenu
          disableRowSelectionOnClick
          editMode={EDIT_MODE}
          hideFooter
          hideFooterSelectedRowCount
          processRowUpdate={processRowUpdate}
          onCellEditStop={handleCellEditStop}
          isCellEditable={(params) => {
            if (isLocked) {
              return false
            }
            if (!String(params.field).startsWith('ec:')) {
              return false
            }
            const categoryId = String(params.field).replace('ec:', '')
            if (isSharedLockedCategory(categoryId, params.row.splitIndex)) {
              return false
            }
            return true
          }}
          initialState={{
            pinnedColumns: {
              right: pinnedRightColumns,
            },
          }}
          pinFirstColumn={false}
          sx={{
            '& .MuiDataGrid-cell': {
              backgroundColor: styleConstants.colors.white,
            },
            '& .MuiDataGrid-pinnedColumnHeaders': {
              backgroundColor: styleConstants.colors.white,
            },
            '& .MuiDataGrid-pinnedColumns': {
              backgroundColor: styleConstants.colors.white,
            },
            '& .MuiDataGrid-row:hover, & .MuiDataGrid-row:hover .MuiDataGrid-cell, & .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedRight':
              {
                backgroundColor: styleConstants.colors.white,
              },
            '& .split-required .headerName::after': {
              content: '" *"',
              color: red300,
            },
            '& .split-locked': {
              cursor: 'default',
            },
            '& .split-locked .text': {
              color: grey700,
              paddingLeft: `${spacing(2)}`,
            },
            '& .split-locked .subtext': {
              color: grey700,
              paddingLeft: `${spacing(2)}`,
            },
          }}
        />
      </div>
      <div
        css={css`
          display: flex;
          gap: ${spacing(4)};
          margin-top: ${spacing(4)};
          margin-bottom: ${spacing(4)};
          align-items: center;
          min-height: ${spacing(10)};
        `}
      >
        {!isLocked && (
          <Button
            variant="text"
            onClick={handleAddRow}
            disabled={!canAddRow}
            css={css`
              cursor: ${canAddRow ? 'pointer' : 'not-allowed'};
            `}
          >
            {`+ ${translate('Add another split')}`}
          </Button>
        )}
        <div
          css={css`
            margin-left: auto;
            display: flex;
            color: ${grey700};
            ${font_md}
            padding-right: ${spacing(18)};
          `}
        >
          <div
            css={css`
              min-width: ${spacing(35)};
              text-align: right;
              padding-right: 3px;
            `}
          >
            <span>{formattedSplitValue}</span>
            <span>{' / '}</span>
            <span
              css={css`
                color: ${text_colorTextPrimary};
              `}
            >
              {formattedTotalAmount}
            </span>
          </div>
        </div>
      </div>
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          margin-top: ${spacing(4)};
        `}
      >
        {!isLocked && props.onSave !== undefined && (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-end;
              gap: ${spacing(1)};
              width: 100%;
            `}
          >
            <Button
              type="button"
              variant={'primary'}
              dropShadow={false}
              disabled={props.disableSave}
              loading={props.saving}
              onClick={() => {
                props.onSave?.(props.splits)
              }}
            >
              {translate('Save Splits')}
            </Button>
            {props.splitError && (
              <span
                css={css`
                  color: ${red400};
                  ${font_sm};
                  width: 100%;
                  text-align: center;
                  margin-top: ${spacing(2)};
                `}
              >
                {props.splitError}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default SplitExpenseCategoriesDataGrid
